import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { RstScoreBadge } from "@/components/RstScoreBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, TrendingUp, Target, Gift, CheckCircle2, Circle, Sparkles, Award, Lock, Check, Trophy, History,
} from "lucide-react";

const PERK_ORDER = ["badge", "visibility", "free_ads", "advanced_stats", "priority_support", "priority_features"];

const Metric = ({ label, value }) => (
  <div className="rounded-2xl bg-card border border-border p-5" data-testid={`progress-metric-${label}`}>
    <p className="font-heading font-extrabold text-2xl text-secondary">{value}</p>
    <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
  </div>
);

const BarChart = ({ data, valueKey, label, currency, symbol }) => {
  const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid={`progress-chart-${valueKey}`}>
      <p className="font-heading font-semibold text-secondary mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> {label}</p>
      <div className="flex items-end gap-2 h-32">
        {data.map((d) => (
          <div key={d.month} className="flex-1 flex flex-col items-center justify-end h-full">
            <span className="text-[10px] text-muted-foreground mb-1">{currency ? "" : (d[valueKey] || 0)}</span>
            <div
              className="w-full rounded-t-md bg-gradient-to-t from-primary/70 to-primary transition-[height] duration-500"
              style={{ height: `${((d[valueKey] || 0) / max) * 100}%`, minHeight: (d[valueKey] || 0) > 0 ? 6 : 2 }}
              title={currency ? formatPrice(d[valueKey] || 0, symbol) : String(d[valueKey] || 0)}
            />
            <span className="text-[10px] text-muted-foreground mt-1">{d.month.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function MyProgress() {
  const { t } = useT();
  const brand = useBrand();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rewards, setRewards] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/me/progress").then((r) => setData(r.data)).catch(() => navigate("/"));
    api.get("/me/rewards").then((r) => setRewards(r.data)).catch(() => {});
  }, [navigate]);

  if (!data) return <div className="container-page py-10"><Skeleton className="h-40 rounded-2xl" /></div>;

  const m = data.metrics;
  const isSeller = data.role === "seller";

  const renderHint = (h) => {
    const vars = { ...h.params };
    if (vars.badge) vars.badge = t(`badge.${vars.badge}`);
    return t(`hint.${h.code}`, vars);
  };

  return (
    <div className="container-page py-10">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 -ml-2 text-muted-foreground hover:text-primary" data-testid="progress-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("common.back")}
      </Button>

      <h1 className="font-heading font-extrabold text-3xl text-secondary">{t("progress.title")}</h1>
      <p className="text-muted-foreground mt-1">{t("progress.subtitle")}</p>

      {/* Tier progress */}
      <div className="mt-6 rounded-2xl bg-secondary text-secondary-foreground p-6 sm:p-8" data-testid="progress-tier-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-secondary-foreground/70 text-sm">{t("progress.currentTier")}</p>
            <div className="mt-1"><RstScoreBadge score={data.rst_score} tier={data.rst_tier} size="md" light /></div>
          </div>
          {data.next_tier ? (
            <div className="text-right">
              <p className="text-secondary-foreground/70 text-sm">{t("progress.nextTier")}</p>
              <p className="font-heading font-bold text-xl">{data.next_tier}</p>
              <p className="text-secondary-foreground/70 text-xs">{t("progress.pointsToNext", { n: data.points_to_next })}</p>
            </div>
          ) : (
            <p className="font-heading font-bold text-lg flex items-center gap-2"><Sparkles className="h-5 w-5" /> {t("progress.maxTier")}</p>
          )}
        </div>
        {data.next_tier && (
          <div className="mt-4">
            <p className="text-xs text-secondary-foreground/70 mb-1.5">{t("progress.progressLabel", { tier: data.next_tier })}</p>
            <div className="h-3 rounded-full bg-white/20 overflow-hidden" data-testid="progress-bar">
              <div className="h-full bg-accent transition-[width] duration-700" style={{ width: `${data.tier_progress_pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Simulator / goals */}
      <section className="mt-8" data-testid="progress-simulator">
        <h2 className="font-heading font-bold text-xl text-secondary mb-3 flex items-center gap-2"><Target className="h-5 w-5 text-primary" /> {t("progress.simulator")}</h2>
        <div className="grid gap-2">
          {data.hints.map((h, i) => (
            <div key={i} className="rounded-xl bg-accent/10 border border-accent/30 p-3 text-sm text-foreground/85 flex items-start gap-2" data-testid={`progress-hint-${i}`}>
              <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {renderHint(h)}
            </div>
          ))}
        </div>
      </section>

      {/* Metrics */}
      <section className="mt-8">
        <h2 className="font-heading font-bold text-xl text-secondary mb-3">{t("progress.metrics")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Metric label={t("progress.delivered")} value={m.delivered} />
          <Metric label={t("progress.totalOrders")} value={m.total_orders} />
          <Metric label={t("progress.successRate")} value={`${m.success_rate}%`} />
          {isSeller && <Metric label={t("progress.responseRate")} value={`${m.response_rate}%`} />}
          {isSeller && <Metric label={t("progress.rating")} value={`${m.rating_avg} (${m.rating_count})`} />}
          {!isSeller && <Metric label={t("progress.paidOrders")} value={m.paid_orders} />}
          <Metric label={t("progress.disputes")} value={`${m.disputes} (${m.dispute_rate}%)`} />
          <Metric label={isSeller ? t("progress.revenue") : t("progress.spend")} value={formatPrice(isSeller ? m.revenue : m.spend, brand.currencySymbol)} />
        </div>
      </section>

      {/* Charts */}
      <section className="mt-8 grid lg:grid-cols-2 gap-4">
        <BarChart data={data.sales_by_month} valueKey="revenue" currency symbol={brand.currencySymbol}
          label={isSeller ? t("progress.salesEvolution") : t("progress.purchasesEvolution")} />
        {isSeller && data.reviews_by_month.length > 0 && (
          <BarChart data={data.reviews_by_month} valueKey="count" label={t("progress.reputationEvolution")} />
        )}
      </section>

      {/* Next badge (seller) */}
      {isSeller && data.next_badge && (
        <section className="mt-8" data-testid="progress-next-badge">
          <h2 className="font-heading font-bold text-xl text-secondary mb-3 flex items-center gap-2"><Award className="h-5 w-5 text-primary" /> {t("progress.nextBadge")}</h2>
          <div className="rounded-2xl bg-card border border-border p-5">
            <p className="font-semibold text-secondary mb-3">{t("progress.badgeGoals", { badge: t(`badge.${data.next_badge.name}`) })}</p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">{data.next_badge.delivered_needed === 0 ? <CheckCircle2 className="h-4 w-4 text-secondary" /> : <Circle className="h-4 w-4 text-muted-foreground" />} {t("progress.needDeliveries", { n: data.next_badge.delivered_needed })}</li>
              <li className="flex items-center gap-2">{data.next_badge.rating_ok ? <CheckCircle2 className="h-4 w-4 text-secondary" /> : <Circle className="h-4 w-4 text-muted-foreground" />} {t("progress.needRating", { r: data.next_badge.rating_target })}</li>
              <li className="flex items-center gap-2">{data.next_badge.dispute_ok ? <CheckCircle2 className="h-4 w-4 text-secondary" /> : <Circle className="h-4 w-4 text-muted-foreground" />} {t("progress.needLowDisputes")}</li>
            </ul>
          </div>
        </section>
      )}

      {/* Missions */}
      {data.missions && data.missions.length > 0 && (
        <section className="mt-8" data-testid="progress-missions">
          <h2 className="font-heading font-bold text-xl text-secondary mb-1 flex items-center gap-2"><Trophy className="h-5 w-5 text-primary" /> {t("missions.title")}</h2>
          <p className="text-sm text-muted-foreground mb-3">{t("missions.subtitle")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.missions.map((mi) => {
              const pct = Math.min(100, Math.round((mi.progress / mi.target) * 100));
              return (
                <div key={mi.code} className={`rounded-2xl border p-4 ${mi.done ? "bg-secondary/5 border-secondary/30" : "bg-card border-border"}`} data-testid={`mission-${mi.code}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-secondary text-sm flex items-center gap-1.5">
                      {mi.done ? <CheckCircle2 className="h-4 w-4 text-secondary" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                      {t(`missions.labels.${mi.code}`)}
                    </p>
                    <span className="shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground">{t("missions.bonusPts", { n: mi.bonus })}</span>
                  </div>
                  <div className="mt-2.5 h-2 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full transition-[width] duration-500 ${mi.done ? "bg-secondary" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs mt-1.5 font-medium">
                    {mi.done ? <span className="text-secondary">{t("missions.completed")}</span>
                      : mi.blocked ? <span className="text-destructive">{t("missions.blocked")}</span>
                      : <span className="text-muted-foreground">{mi.progress} / {mi.target}</span>}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Historique des récompenses */}
      <section className="mt-8" data-testid="progress-rewards">
        <h2 className="font-heading font-bold text-xl text-secondary mb-1 flex items-center gap-2"><History className="h-5 w-5 text-primary" /> {t("progress.rewardsTitle")}</h2>
        <p className="text-sm text-muted-foreground mb-3">{t("progress.rewardsSubtitle")}</p>
        {rewards.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-xl bg-card border border-dashed border-border p-4">{t("progress.rewardsEmpty")}</p>
        ) : (
          <div className="rounded-2xl bg-card border border-border divide-y divide-border/60 overflow-hidden">
            {rewards.map((ev) => {
              const isTier = ev.type === "tier";
              const label = isTier ? ev.label : t(`missions.labels.${ev.label}`);
              return (
                <div key={ev.id} className="flex items-center gap-3 p-3.5" data-testid={`reward-${ev.type}`}>
                  <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${isTier ? "bg-accent/20 text-accent-foreground" : "bg-secondary/10 text-secondary"}`}>
                    {isTier ? <Sparkles className="h-5 w-5" /> : <Trophy className="h-5 w-5" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-secondary text-sm truncate">{label}</p>
                    <p className="text-xs text-muted-foreground">{isTier ? t("progress.rewardTier") : t("progress.rewardMission")} · {new Date(ev.created_at).toLocaleDateString()}</p>
                  </div>
                  {ev.points > 0 && (
                    <span className="shrink-0 text-sm font-bold text-secondary">+{ev.points}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Perks */}
      <section className="mt-8" data-testid="progress-perks">
        <h2 className="font-heading font-bold text-xl text-secondary mb-3 flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> {t("progress.myPerks")}</h2>
        {data.current_perks.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-xl bg-card border border-dashed border-border p-4">{t("progress.noPerks")}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {data.current_perks.map((p) => (
              <span key={p} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary/10 text-secondary text-sm font-medium" data-testid={`progress-perk-${p}`}>
                <Check className="h-4 w-4" /> {t(`perks.${p}`)}
              </span>
            ))}
          </div>
        )}

        <h3 className="font-heading font-semibold text-secondary mt-6 mb-3">{t("progress.allTiers")}</h3>
        <div className="grid gap-3">
          {data.all_tiers.map((tier) => {
            const reached = data.rst_score >= tier.min;
            return (
              <div key={tier.name} className={`rounded-2xl border p-4 ${reached ? "bg-card border-secondary/30" : "bg-muted/30 border-border"}`} data-testid={`progress-tier-${tier.name}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-heading font-semibold text-secondary flex items-center gap-2">
                    {reached ? <CheckCircle2 className="h-4 w-4 text-secondary" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                    {tier.name} <span className="text-xs text-muted-foreground font-normal">· {tier.min}+</span>
                  </p>
                </div>
                {(tier.perks || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[...(tier.perks || [])].sort((a, b) => PERK_ORDER.indexOf(a) - PERK_ORDER.indexOf(b)).map((p) => (
                      <span key={p} className="text-[11px] px-2 py-0.5 rounded-full bg-accent/15 text-accent-foreground">{t(`perks.${p}`)}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">—</p>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-4 max-w-2xl">{t("perks.note")}</p>
      </section>
    </div>
  );
}
